import React from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';

function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
}));

export default function LocationInput(props) {
  const classes = useStyles();
  const [value, setValue] = React.useState(props.defaultLocation);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState([]);
	const loaded = React.useRef(false);
	const geocoder = React.useRef();

	const inputStates = {
		default: {
			error: false,
			message: ""
		},
		empty: {
			error: true,
			message: "An exact location is required."
		}
	}
	const [inputState, setInputState] = React.useState(inputStates.default);

	React.useEffect(() => {
		setInputState(inputStates[props.status || "default"])
	}, [props.status])
	
  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        'https://maps.googleapis.com/maps/api/js?key=AIzaSyA44U_odQe2p7H_22f5KvSeiKIMLO58sWg&libraries=places',
        document.querySelector('head'),
        'google-maps',
      );
    }

    loaded.current = true;
  }

  const fetch = React.useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 200),
    [],
  );

  React.useEffect(() => {
    let active = true;

		if (!autocompleteService.current && window.google) {
			autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
		if (!geocoder.current && window.google) {
			geocoder.current = new window.google.maps.Geocoder();
		}
		if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results) => {
      if (active) {
        let newOptions = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <Autocomplete
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
			freeSolo
			value={value}
			size="small"
	    onChange={(event, newValue, reason) => {
        setOptions(newValue ? [newValue, ...options] : options);
				setValue(newValue);

				if (reason == "clear") {
					props.onChange(null);					
				} else if (reason == "select-option") {
					geocoder.current.geocode( {placeId: newValue.place_id}, function(results, status) {
						if (status == 'OK') {
							let locationData = {};

							let hasLocality = false;
							let hasTown = false;
							results[0].address_components.forEach(e => {
								if (e.types.includes("locality")) {
									locationData.city = e.long_name;
									hasLocality = true;
								} else if (e.types.includes("postal_town") && !hasLocality) {
									locationData.city = e.long_name;
									hasTown = true;
								} else if (e.types.includes("sublocality") && !hasLocality && !hasTown) {
									locationData.city = e.long_name
								} else if (e.types.includes("country")) {
									locationData.country = e.long_name
									locationData.country_short = e.short_name
								} else if (e.types.includes("administrative_area_level_1")) {
									locationData.administrative1 = e.long_name
									locationData.administrative1_short = e.short_name
								} else if (e.types.includes("postal_code")) {
									locationData.zip = e.long_name
								}
							})

							locationData.full_address = results[0].formatted_address
							locationData.lat = results[0].geometry.location.lat()
							locationData.lng = results[0].geometry.location.lng()
							locationData.types = results[0].types

							props.onChange(locationData)						
							// console.log(locationData)
						} else {
							alert('Geocode was not successful for the following reason: ' + status);
						}
					});
				}
			}}

      onInputChange={(event, newInputValue) => {
				setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
					{...params}
					{...props.inputProps}
					required
					error={inputStates[props.status || "default"].error}
					helperText={inputStates[props.status || "default"].message}
					onFocus={() => props.setStatus && props.setStatus("default")}
					label="Location"
					variant="outlined"
					placeholder='Start typing...'
					fullWidth
				/>
      )}
      renderOption={(option) => {
        const matches = option.structured_formatting.main_text_matched_substrings;
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length]),
        );

        return (
          <Grid container alignItems="center">
            <Grid item>
              <LocationOnIcon className={classes.icon} />
            </Grid>
            <Grid item xs>
              {parts.map((part, index) => (
                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                  {part.text}
                </span>
              ))}

              <Typography variant="body2" color="textSecondary">
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
          </Grid>
        );
      }}
    />
  );
}
